// Vault connections data layer (#312). Reads `GET /v1/secrets` — names +
// timestamps ONLY (the sealed-list contract: a read never yields plaintext) —
// and drives save / revoke of `dashboard:<handle>` credentials, the tokens
// `.maintainer.yml` references as `token_ref: dashboard:<handle>` (e.g. for
// `mcp_servers[]`). Mirrors `lib/integrations.ts`; the value only lives in this
// layer for the single POST and is sealed server-side on arrival.

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch, withCursor } from './api';
import { type Page, type PagedList, usePagedQuery } from './paged';
import { SESSION_TOKEN } from './session';

/** Client-safe vault row — the shape `toView` returns in `apps/api/src/routes/secrets.ts`. */
export type SecretRow = {
  readonly name: string;
  readonly created_at: string;
};

export const SECRETS_QUERY_KEY = 'secrets';

/**
 * ONE page of the account's vault entries (metadata only), at `cursor`. Idle
 * (no fetch) when there's no session, like `useMe`. Hand it to `usePagedQuery`:
 * the cursor accessor is part of the query key, so the walk refetches itself.
 */
function useSecretsPage(cursor: () => string | null, token: string | null = SESSION_TOKEN) {
  return createQuery<Page<SecretRow>>(() => ({
    queryKey: [SECRETS_QUERY_KEY, cursor(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: async () => {
      const page = await apiFetch<Page<SecretRow>>(withCursor('/v1/secrets', cursor()), token);
      // `?? null` normalises an envelope that omits the field: `undefined` there
      // would arm "Load more" over a page that does not exist.
      return { data: page.data, next_cursor: page.next_cursor ?? null };
    },
  }));
}

/**
 * The account's vault entries, walked WHOLE. `exhaust` is what makes the panel's
 * client-side `dashboard:`/`integration:` split honest: `SECRET_LIST_SPEC`
 * advertises sort only (no `filter`), so the namespace can only be separated
 * client-side — and over page one alone a `dashboard:` row that sorted onto page
 * two was neither listed nor revokable (#1442). `name` is the row identity: the
 * projection carries no `id` and a name is account-unique (`secrets_account_name_idx`).
 */
export function useSecrets(token: string | null = SESSION_TOKEN): PagedList<SecretRow> {
  return usePagedQuery<SecretRow>((cursor) => useSecretsPage(cursor, token), {
    key: 'name',
    exhaust: true,
  });
}

/** Save (or rotate) a dashboard-managed credential. Returns the full `dashboard:<handle>` ref. */
export async function saveConnectionToken(
  input: { name: string; value: string },
  token: string | null = SESSION_TOKEN,
): Promise<{ name: string }> {
  return apiFetch<{ name: string }>('/v1/secrets', token, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Revoke a dashboard-managed credential by its full `dashboard:<handle>` ref. */
export async function deleteConnectionToken(
  name: string,
  token: string | null = SESSION_TOKEN,
): Promise<{ name: string }> {
  return apiFetch<{ name: string }>(`/v1/secrets/${encodeURIComponent(name)}`, token, {
    method: 'DELETE',
  });
}
